/**
 * Tenant timezone (WSRADM-0047 / WSRAPI-0152).
 *
 * `GET`/`PUT /admin/settings/timezone` via `apiBase()`. Tenant is the JWT — never a
 * slug on the path, query, or header. Bearer only; no cookies, no CSRF. Never
 * `/admin/platform/**`. `source: 'DEFAULT'` means nothing is set and the tenant runs
 * on UTC. There is NO delete or reset — the setting can only be changed, never
 * cleared — so none is offered here.
 */
import { request } from '@/api/client';
import { ApiError } from '@/api/errors';
import { asBoolean, asNumber, asText, isObject } from '@/api/parse';

export type TimezoneSource = 'SETTINGS' | 'DEFAULT';

export interface TimezoneSetting {
  tenantSlug: string;
  timezone: string;
  source: TimezoneSource;
}

export interface TimezoneBuild {
  id: number;
  status: string;
  jobType: string;
  alreadyInFlight: boolean;
}

export interface TimezoneSaveResult {
  tenantSlug: string;
  timezone: string;
  previousTimezone: string | null;
  changed: boolean;
  rebuildMarked: boolean;
  build: TimezoneBuild | null;
}

export function parseTimezoneSetting(body: unknown): TimezoneSetting {
  if (!isObject(body)) {
    throw new ApiError('UNKNOWN', 0, {});
  }
  return {
    tenantSlug: asText(body['tenantSlug']) ?? '',
    timezone: asText(body['timezone']) ?? 'UTC',
    // Unknown source degrades to the safe reading: nothing saved.
    source: body['source'] === 'SETTINGS' ? 'SETTINGS' : 'DEFAULT',
  };
}

function parseBuild(raw: unknown): TimezoneBuild | null {
  if (!isObject(raw)) return null;
  const id = asNumber(raw['id']);
  if (id === null) return null;
  return {
    id,
    status: asText(raw['status']) ?? '',
    jobType: asText(raw['jobType']) ?? '',
    alreadyInFlight: asBoolean(raw['alreadyInFlight']) === true,
  };
}

export function parseTimezoneSaveResult(body: unknown): TimezoneSaveResult {
  if (!isObject(body)) {
    throw new ApiError('UNKNOWN', 0, {});
  }
  return {
    tenantSlug: asText(body['tenantSlug']) ?? '',
    timezone: asText(body['timezone']) ?? '',
    previousTimezone: asText(body['previousTimezone']),
    changed: asBoolean(body['changed']) === true,
    rebuildMarked: asBoolean(body['rebuildMarked']) === true,
    build: parseBuild(body['build']),
  };
}

/** GET /admin/settings/timezone */
export async function getTimezone(): Promise<TimezoneSetting> {
  const body = await request<unknown>('GET', '/admin/settings/timezone');
  return parseTimezoneSetting(body);
}

/** PUT /admin/settings/timezone — body `{ timezone }`. */
export async function putTimezone(timezone: string): Promise<TimezoneSaveResult> {
  const body = await request<unknown>('PUT', '/admin/settings/timezone', {
    json: { timezone },
  });
  return parseTimezoneSaveResult(body);
}

/** Backend envelope code `INVALID_TIMEZONE` (details['code']). */
export function isInvalidTimezone(err: unknown): boolean {
  return err instanceof ApiError && err.details?.['code'] === 'INVALID_TIMEZONE';
}
